/**
 * PlaneConnectModal — paste-token (api-key) form for the Plane connector.
 *
 * Plane auth is an API KEY, not OAuth. The user pastes THREE values:
 *   - API key        (personal API token from Plane → Settings → API tokens)
 *   - Workspace slug  (the workspace to operate on)
 *   - Base URL        (optional; defaults to https://api.plane.so for Plane
 *                      Cloud. Self-hosted / Zibby-hosted instances set their
 *                      own API base.)
 *
 * Backend validates (lists the workspace's projects) before persisting —
 * structured error surfaces on err.message. Mirrors the other paste-token
 * connect modals for the shared shape; adds the two extra fields.
 */

import React, { useEffect, useState } from 'react';
import { IonModal, IonButton, IonSpinner } from '@ionic/react';
import { useConnectPlane } from '../../hooks/useIntegrations';
import '../../pages/IntegrationsPage/IntegrationsPage.css';

const DEFAULT_BASE_URL = 'https://api.plane.so';

export default function PlaneConnectModal({ isOpen, onClose, onConnected }) {
  const [apiKey, setApiKey] = useState('');
  const [workspaceSlug, setWorkspaceSlug] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [error, setError] = useState('');
  const [connecting, setConnecting] = useState(false);
  const mutation = useConnectPlane();

  useEffect(() => {
    if (isOpen) {
      setApiKey('');
      setWorkspaceSlug('');
      setBaseUrl('');
      setError('');
      setConnecting(false);
    }
  }, [isOpen]);

  const handleSubmit = () => {
    if (!apiKey.trim()) {
      setError('API key is required.');
      return;
    }
    if (!workspaceSlug.trim()) {
      setError('Workspace slug is required.');
      return;
    }
    setConnecting(true);
    setError('');
    mutation.mutate(
      {
        apiKey: apiKey.trim(),
        workspaceSlug: workspaceSlug.trim(),
        // Empty → backend defaults to https://api.plane.so.
        baseUrl: baseUrl.trim(),
      },
      {
        onSuccess: () => {
          setConnecting(false);
          onConnected?.();
          onClose?.();
        },
        onError: (err) => {
          setConnecting(false);
          setError(err?.message || 'Failed to connect Plane. Please check your API key and workspace slug.');
        },
      },
    );
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={() => onClose?.()}
      className="lark-connect-modal"
      aria-label="Connect Plane"
    >
      <div
        className="lark-modal-content"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2>Connect Plane</h2>
        <p className="lark-modal-description">
          Create a personal API token in Plane → Workspace Settings → API tokens, then
          paste it below along with your workspace slug. Self-hosted or Teamy-hosted Plane?
          Set the base URL to your instance&apos;s API base.
        </p>

        <div className="lark-form-group">
          <label className="lark-form-label" htmlFor="plane-api-key">API Key</label>
          <input
            id="plane-api-key"
            type="password"
            value={apiKey}
            placeholder="plane_api_..."
            onChange={(e) => { setApiKey(e.target.value || ''); setError(''); }}
            className="lark-form-input"
            disabled={connecting}
            autoComplete="off"
            spellCheck={false}
            aria-invalid={error ? 'true' : 'false'}
          />
        </div>

        <div className="lark-form-group">
          <label className="lark-form-label" htmlFor="plane-workspace-slug">Workspace Slug</label>
          <input
            id="plane-workspace-slug"
            type="text"
            value={workspaceSlug}
            placeholder="my-workspace"
            onChange={(e) => { setWorkspaceSlug(e.target.value || ''); setError(''); }}
            className="lark-form-input"
            disabled={connecting}
            autoComplete="off"
            spellCheck={false}
          />
        </div>

        <div className="lark-form-group">
          <label className="lark-form-label" htmlFor="plane-base-url">Base URL</label>
          <input
            id="plane-base-url"
            type="text"
            value={baseUrl}
            placeholder={DEFAULT_BASE_URL}
            onChange={(e) => { setBaseUrl(e.target.value || ''); setError(''); }}
            className="lark-form-input"
            disabled={connecting}
            autoComplete="off"
            spellCheck={false}
          />
          <p className="lark-form-hint" style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
            Default {DEFAULT_BASE_URL} (Plane Cloud). Self-hosted / Teamy-hosted: your instance&apos;s API base.
          </p>
        </div>

        {error && <p className="lark-modal-error" role="alert">{error}</p>}

        <div className="lark-modal-buttons">
          <IonButton
            expand="block"
            onClick={handleSubmit}
            disabled={connecting || !apiKey.trim() || !workspaceSlug.trim()}
            aria-label="Submit Plane credentials"
          >
            {connecting ? <IonSpinner name="crescent" /> : 'Connect'}
          </IonButton>
          <IonButton
            expand="block"
            fill="clear"
            onClick={() => onClose?.()}
            disabled={connecting}
            aria-label="Cancel Plane connection"
          >
            Cancel
          </IonButton>
        </div>
      </div>
    </IonModal>
  );
}
